import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Typography,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
  Grid,
  Alert,
  Tooltip,
  Button,
  Stack,
  Paper,
} from '@mui/material';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Table from '../../../common/components/Table/Table';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems, usePermission } from '../../../hooks';
import { MainContent } from './ENO1TempOverview.styled';
import { ContentPaper } from './ViewSamples.styled';
import { ContextMenuAction } from '../../../common/components/Table/types';
import { useTempSamplesData, TempSamplesData } from './hooks';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { ValidationError } from '../../../hooks/useFileUpload';

// Custom hook for handling paginated errors
function usePaginatedErrors(errors: ValidationError[]) {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' } | null>(null);

  // Ensure errors are properly formatted and assign IDs
  const processedErrors = useMemo(() => {
    if (!Array.isArray(errors)) {
      console.error('Errors is not an array:', errors);
      return [];
    }
    
    const processed = errors.map((error, index) => {
      // Ensure error has all required fields or provide defaults
      if (!error || typeof error !== 'object') {
        console.warn(`Invalid error at index ${index}:`, error);
        return {
          id: `error-${index}`,
          sampleId: null,
          field: 'unknown',
          errorCode: 'UnknownError',
          message: 'An unknown error occurred'
        };
      }
      
      return {
        id: `error-${index}`, // Add id for table row key
        sampleId: error.sampleId ?? null,
        field: error.field || 'unknown',
        errorCode: error.errorCode || 'Error',
        message: error.message || 'No error message provided'
      };
    });

    // Apply sorting if configured
    if (sortConfig) {
      return [...processed].sort((a, b) => {
        const aValue = a[sortConfig.field as keyof typeof a];
        const bValue = b[sortConfig.field as keyof typeof b];
        
        if (aValue === null) return sortConfig.direction === 'ASC' ? -1 : 1;
        if (bValue === null) return sortConfig.direction === 'ASC' ? 1 : -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ASC' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Default comparison for other types
        return sortConfig.direction === 'ASC' 
          ? (aValue < bValue ? -1 : 1) 
          : (aValue > bValue ? -1 : 1);
      });
    }
    
    return processed;
  }, [errors, sortConfig]);

  // Calculate total error count
  const totalErrorCount = processedErrors.length;

  // Get the paginated slice of errors for the current page
  const paginatedErrors = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return processedErrors.slice(startIndex, endIndex);
  }, [processedErrors, currentPage, pageSize]);

  // Sort errors based on field and direction
  const sortErrors = useCallback((field: string, direction: 'ASC' | 'DESC') => {
    setSortConfig({ field, direction });
    setCurrentPage(0); // Reset to first page when sorting
  }, []);

  return {
    paginatedErrors,
    totalErrorCount,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortErrors
  };
}

// Custom Error Table Component that handles its own pagination
const PaginatedErrorTable: React.FC<{ errors: ValidationError[], fileName?: string }> = ({ errors, fileName }) => {
  const {
    paginatedErrors,
    totalErrorCount,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortErrors
  } = usePaginatedErrors(errors);

  // Define table columns with fixed widths to ensure alignment
  const columns = useMemo(() => [
    {
      Header: 'Sample ID',
      accessor: 'sampleId',
      width: '15%', // Fixed width to ensure alignment
      Cell: ({ value }: { value: string | null }) => (
        value !== null ? value : '-'
      )
    },
    {
      Header: 'Field',
      accessor: 'field',
      width: '15%', // Fixed width to ensure alignment
    },
    {
      Header: 'Error Code',
      accessor: 'errorCode',
      width: '20%', // Fixed width to ensure alignment
    },
    {
      Header: 'Message',
      accessor: 'message',
      width: '49%', // Fixed width to ensure alignment
      Cell: ({ value }: { value: string }) => (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',  // Allow text to wrap to new lines
            wordBreak: 'break-word', // Break words to prevent overflow
            overflowWrap: 'break-word', // Ensure long words break and wrap
            lineHeight: 1.5,         // Increase line height for better readability
            padding: '8px 0',        // Add some padding for better spacing
          }}
        >
          {value}
        </Typography>
      )
    }
  ], []);

  // Handle sorting
  const handleSort = (column: string, direction: 'ASC' | 'DESC' | undefined, searchColumn?: string, searchTerm?: string) => {
    if (direction) {
      sortErrors(column, direction);
    }
  };

  // Early return if there are no valid errors
  if (totalErrorCount === 0) {
    return null;
  }

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        width: '100%', 
        mt: 2,
        overflow: 'hidden', // Prevents horizontal overflow
        '& .MuiTableContainer-root': {
          overflow: 'auto'
        },
        '& .MuiTable-root': {
          tableLayout: 'fixed' // Forces the table to respect column widths
        }
      }}
      data-testid="paginated-error-table"
    >
      <Table
        data={paginatedErrors}
        columns={columns}
        moduleName={`Validation Errors for ${fileName || 'file'} (${totalErrorCount})`}
        pageSize={pageSize}
        page={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        totalCount={totalErrorCount}
        onSort={handleSort}
        viewMode={true} // Set to true to hide action buttons
        isRowClickable={false}
        disableAutoSizing={true} // Disable auto-sizing to use our fixed widths
        data-testid="validation-errors-table"
      />
    </Paper>
  );
};

const ViewSamples: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fileId } = useParams<{ fileId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/eno1-temp-overview', '');

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // State for notification messages
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  // Get pagination parameters from URL
  const page = parseInt(searchParams.get('page') || '0', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const sortField = searchParams.get('sort') || '';
  const sortDirection = (searchParams.get('direction') || '') as 'ASC' | 'DESC';

  const numericFileId = fileId ? parseInt(fileId, 10) : 0;
  const isValidFileId = !isNaN(numericFileId) && numericFileId > 0;

  // Extract search parameters from URL on component mount
  useEffect(() => {
    const searchColParam = searchParams.get('searchColumn');
    const searchTermParam = searchParams.get('searchTerm');
    
    if (searchColParam) setSearchColumn(searchColParam);
    if (searchTermParam) setSearchTerm(searchTermParam);
  }, [searchParams]);

  const [searchColumn, setSearchColumn] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  // Fetch temp samples data
  const {
    data: tempSamplesResponse,
    isLoading: isDataLoading,
    isError: isDataError,
    error: dataError,
  } = useTempSamplesData({
    eno1FileId: numericFileId,
    page,
    size: pageSize,
    sort: sortField,
    direction: sortDirection,
    searchColumn: searchColumn,
    searchTerm: searchTerm,
    enabled: isValidFileId,
  });

  // Prepare data for the table
  const samplesData = useMemo(() => {
    return tempSamplesResponse?.content || [];
  }, [tempSamplesResponse]);

  const handleBack = () => {
    navigate('/sampling-planning/temp-overview');
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/eno1-temp-overview');
  };

  // Reset validation errors
  const resetValidationErrors = () => {
    setValidationErrors([]);
  };

  // Update URL when pagination parameters change
  const updateSearchParams = (params: Record<string, string>) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Update the search params with the new values
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });

    // Update the URL without reloading the page
    setSearchParams(newSearchParams);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    updateSearchParams({ page: newPage.toString() });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    // First clear the size parameter to force a query parameter change
    const tempParams = new URLSearchParams(searchParams);
    tempParams.delete('size');
    tempParams.delete('page');
    setSearchParams(tempParams);

    // Then set it again with a slight delay to ensure URL changes are processed
    setTimeout(() => {
      updateSearchParams({
        page: '0', // Reset to first page when changing page size
        size: newSize.toString(),
      });
    }, 0);
  };

  // Handle sorting
  const handleSort = (column: string, direction: 'ASC' | 'DESC' | undefined, searchColumn?: string, searchTerm?: string) => {
    setSearchColumn(searchColumn);
    setSearchTerm(searchTerm);
    
    const params: Record<string, string> = {};
    
    // Only set sort params if direction is provided and not undefined
    if (direction && direction !== 'undefined' as any) {
      params.sort = column;
      params.direction = direction;
    }
    
    if (searchColumn && searchTerm) {
      params.searchColumn = searchColumn;
      params.searchTerm = searchTerm;
    } else {
      // Clear search params if not provided
      const tempSearchParams = new URLSearchParams(searchParams);
      tempSearchParams.delete('searchColumn');
      tempSearchParams.delete('searchTerm');
      setSearchParams(tempSearchParams);
    }
    
    updateSearchParams(params);
  };


  // Define columns for the table
  const columns = useMemo(
    () => [
      // Main columns in specified order
      {
        Header: 'sample_status',
        accessor: 'sampleStatus',
      },
      {
        Header: 'ASI',
        accessor: 'asi',
        width: '70px',
        Cell: ({ value }: { value: any }) => {
          if (value === true) return 'Y';
          if (value === false) return '';
          return value !== null && value !== undefined ? String(value) : '';
        },
      },
      {
        Header: 'sample_id',
        accessor: 'sampleId',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Explicitly convert to string for display
        },
      },
      {
        Header: 'version',
        accessor: 'version',
      },
      {
        Header: 'change_by',
        accessor: 'changeBy',
      },
      {
        Header: 'comment',
        accessor: 'comment',
        Cell: ({ value }: { value: any }) => {
          if (value === true) return 'Y';
          if (value === false) return '';
          return value !== null && value !== undefined ? String(value) : '';
        },
      },
      {
        Header: 'del_flag',
        accessor: 'delFlag',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'valid_flag',
        accessor: 'validFlag',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'mail_category',
        accessor: 'mailCategory',
      },
      {
        Header: 'format_corrected',
        accessor: 'formatCorrected',
      },
      {
        Header: 'sampling_method',
        accessor: 'samplingMethod',
      },
      {
        Header: 'net_or_gross_accounting',
        accessor: 'netOrGrossAccounting',
      },
      {
        Header: 'sampling_rate',
        accessor: 'samplingRate',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'sending_ppo',
        accessor: 'sendingPpo',
      },
      {
        Header: 'oe_from_code',
        accessor: 'oeFromCode',
      },
      {
        Header: 'receiving_ppo',
        accessor: 'receivingPpo',
      },
      {
        Header: 'oe_to_code',
        accessor: 'oeToCode',
      },
      {
        Header: 'sampling_date',
        accessor: 'samplingDate',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'receptacle_number',
        accessor: 'receptacleNumber',
      },
      {
        Header: 'despatch_number',
        accessor: 'despatchNumber',
      },
      {
        Header: 'despatch_date',
        accessor: 'despatchDate',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'gross_kg',
        accessor: 'grossKg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'tare_kg',
        accessor: 'tareKg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'net_kg',
        accessor: 'netKg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'p_kg',
        accessor: 'pkg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'g_kg',
        accessor: 'gkg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'e_kg',
        accessor: 'ekg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'p_items',
        accessor: 'pitems',
      },
      {
        Header: 'g_items',
        accessor: 'gitems',
      },
      {
        Header: 'e_items',
        accessor: 'eitems',
      },
      {
        Header: 'despatch_mail_subclass',
        accessor: 'despatchMailSubclass',
      },
      {
        Header: 'subsample_rate',
        accessor: 'subsampleRate',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'stratum',
        accessor: 'stratum',
      },
      {
        Header: 'received_weight_stratum',
        accessor: 'receivedWeightStratum',
      },
      {
        Header: 'sampled_weight_stratum',
        accessor: 'sampledWeightStratum',
      },
      {
        Header: 'effective_sampling_rate_stratum',
        accessor: 'effectiveSamplingRateStratum',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'barcode',
        accessor: 'barcode',
      },
      {
        Header: 'r+v',
        accessor: 'rplusV',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'f_bag',
        accessor: 'fbag',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'receptacle_type',
        accessor: 'receptacleType',
      },
      {
        Header: 'receptacle_category',
        accessor: 'receptacleCategory',
      },
      {
        Header: 'receptacle_mail_subclass',
        accessor: 'receptacleMailSubclass',
      },
      {
        Header: 'label_weight',
        accessor: 'labelWeight',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '';
          return String(value);
        },
      },
      {
        Header: 'ibrs_kg',
        accessor: 'ibrsKg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'format_announced',
        accessor: 'formatAnnounced',
      },
      {
        Header: 'c_kg',
        accessor: 'ckg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'c_items',
        accessor: 'citems',
      },
      {
        Header: 'received_receptacles_stratum',
        accessor: 'receivedReceptaclesStratum',
      },
    ],
    []
  );

  // Define which columns are essential for mobile view
  const essentialColumns = [
    'sample_id',
    'barcode',
    'sample_status',
    'version',
    'mail_category',
    'gross_kg',
    'net_kg',
  ];

  // Filter columns based on screen size
  const filteredColumns = useMemo(() => {
    // On mobile, only show essential columns
    if (isMobile) {
      return columns.filter((column) => essentialColumns.includes(column.accessor as string));
    }
    // On desktop, show all columns
    return columns;
  }, [columns, isMobile, essentialColumns]);

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const getTableContent = () => {
    if (!isValidFileId) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant='body1' color='error' data-testid="invalid-file-error">
            {t('tempOverview.viewSamples.invalidFileId', 'Invalid file ID provided')}
          </Typography>
        </Box>
      );
    }

    if (isDataLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="loading-indicator">
          <CircularProgress />
        </Box>
      );
    }

    if (isDataError) {
      return (
        <Box sx={{ p: 2, color: 'error.main' }} data-testid="error-container">
          <Typography variant='body1'>
            {t('tempOverview.viewSamples.error', 'Error loading sample data')}
          </Typography>
          {dataError instanceof Error && (
            <Typography variant='body2' sx={{ mt: 1 }}>
              {dataError.message}
            </Typography>
          )}
        </Box>
      );
    }

    if (samplesData.length === 0) {
      return (
        <Box sx={{ p: 2 }} data-testid="no-data-container">
          <Typography variant='body1'>
            {t('tempOverview.viewSamples.noData', 'No sample data available for this file')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }} data-testid="mobile-swipe-hint">
            <Tooltip title='Swipe left and right to view all data columns'>
              <InfoIcon color='primary' sx={{ mr: 1, fontSize: 18 }} />
            </Tooltip>
            <Typography variant='caption' color='text.secondary'>
              {t(
                'tempOverview.viewSamples.mobileNote',
                'Swipe horizontally to view all columns. Only showing essential columns on mobile.'
              )}
            </Typography>
          </Box>
        )}

        {/* Display validation errors if they exist */}
        {validationErrors.length > 0 && (
          <Box sx={{ mb: 3 }} data-testid="validation-errors-container">
            <PaginatedErrorTable 
              errors={validationErrors} 
              fileName={`Temp Overview File ID: ${fileId}`}
            />
          </Box>
        )}
        
        <Table
          data={samplesData}
          columns={filteredColumns}
          moduleName={t(
            'tempOverview.viewSamples.tableTitle',
            `TEMP OVERVIEW SAMPLES`
          )}
          totalCount={tempSamplesResponse?.totalElements || 0}
          pageSize={pageSize}
          page={page}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
          onSort={handleSort}
          defaultSort={{ column: sortField, direction: sortDirection }}
          initialSearchColumn={searchColumn}
          initialSearchTerm={searchTerm}
          disableAutoSizing={false}
          viewMode={true} // Set viewMode to true for read-only view
          isRowClickable={false}
          data-testid="samples-table"
        />
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid="temp-overview-samples-container">
      <Grid container data-testid="temp-samples-grid-container">
        {/* First Row - SectionBar */}
        <Grid item xs={12} data-testid="temp-samples-section-bar-container">
          <SectionBar
            title={`Temp Overview Samples - File ID: ${fileId}`}
            subtitle='Sampling Planning'
            onToggleSidebar={handleSidebarToggle}
            onSubtitleClick={handleSubtitleClick}
            isSidebarOpen={sidebarOpen}
            showFilters={{}}
            data-testid="temp-samples-section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }} data-testid="temp-samples-main-row">
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} height='100%' data-testid="temp-samples-sidebar-container">
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="temp-samples-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='temp-overview-samples-main-content'>
            {permissionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="temp-samples-loading-indicator">
                <CircularProgress data-testid="temp-samples-loading-spinner" />
              </Box>
            ) : !hasPermission ? (
              <Alert severity='warning' data-testid="temp-samples-permission-error-alert">You don't have permission to view this section</Alert>
            ) : (
              <Box component={Paper} sx={{ mb: 2 }} data-testid="temp-samples-content-paper-container">
                <ContentPaper data-testid="temp-samples-content-paper">{getTableContent()}</ContentPaper>
              </Box>
            )}
          </MainContent>
        </Grid>

        {/* Notification Snackbar */}
        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          severity={notification.type}
          onClose={handleNotificationClose}
          data-testid="temp-samples-notification-snackbar"
        />
      </Grid>
    </Box>
  );
};

export default ViewSamples;
